import { useState, useEffect, useRef, useCallback } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

// ── Official SBU Brand Colors (from marcom guidelines) ─────────────────────
const SBU = {
  red:       "#CC0033",   // Stony Brook Red (primary)
  redBright: "#E8002D",   // Stony Brook Bright Red
  redDark:   "#8C0023",   // Stony Brook Dark Red
  navy:      "#221E1F",   // near-black
  charcoal:  "#3D3535",
  midgray:   "#6B6262",
  lightgray: "#F2F0F0",
  silver:    "#E0DCDC",
  white:     "#FFFFFF",
};

const DEPT_META = {
  "Applied Mathematics & Statistics":        { short:"AMS", icon:"∑"  },
  "Biomedical Engineering":                  { short:"BME", icon:"🫀" },
  "Biomedical Informatics":                  { short:"BMI", icon:"🧬" },
  "Civil Engineering":                       { short:"CEE", icon:"🏗"  },
  "Computer Science":                        { short:"CS",  icon:"💻" },
  "Electrical Engineering":                  { short:"ECE", icon:"⚡" },
  "Materials Science & Chemical Engineering":{ short:"MSE", icon:"⚗"  },
  "Mechanical Engineering":                  { short:"MAE", icon:"⚙"  },
  "Technology & Society":                    { short:"TAS", icon:"🌐" },
};

const TITLE_RANK = {
  "Distinguished Leading Professor": { label:"Distinguished Leading", tier:6 },
  "Distinguished Professor":         { label:"Distinguished",         tier:5 },
  "Leading Professor":               { label:"Leading Professor",     tier:4 },
  "Distinguished Teaching Professor":{ label:"Dist. Teaching",        tier:4 },
  "Professor":                       { label:"Professor",             tier:3 },
  "Associate Professor":             { label:"Associate Professor",   tier:2 },
  "Assistant Professor":             { label:"Assistant Professor",   tier:1 },
};

function getInitials(name) {
  return name.split(" ").slice(0,2).map(p=>p[0]).join("").toUpperCase();
}

function useDebounce(value, delay) {
  const [d, setD] = useState(value);
  useEffect(() => { const t = setTimeout(()=>setD(value), delay); return ()=>clearTimeout(t); }, [value, delay]);
  return d;
}

// ── SBU Prism SVG — geometric triangles in official red palette ────────────
function PrismDecoration({ style }) {
  return (
    <svg viewBox="0 0 400 300" style={{ position:"absolute", ...style }} aria-hidden="true">
      {/* Large primary red triangles */}
      <polygon points="320,0 400,0 400,120"   fill={SBU.red}       opacity="0.9" />
      <polygon points="400,120 400,220 300,220" fill={SBU.redDark}   opacity="0.85" />
      <polygon points="300,0 380,0 300,100"   fill={SBU.redBright}  opacity="0.7" />
      {/* Medium triangles */}
      <polygon points="350,150 400,100 400,220" fill={SBU.red}      opacity="0.5" />
      <polygon points="260,0 320,0 260,70"    fill={SBU.redDark}    opacity="0.6" />
      <polygon points="370,220 400,220 400,300 370,300" fill={SBU.redBright} opacity="0.4" />
      {/* Small accent triangles */}
      <polygon points="240,0 270,0 240,40"    fill={SBU.redBright}  opacity="0.5" />
      <polygon points="400,250 370,300 400,300" fill={SBU.redDark}  opacity="0.7" />
      <polygon points="340,180 380,140 380,210" fill={SBU.red}      opacity="0.35" />
    </svg>
  );
}

// ── Small corner prism for cards/headers ──────────────────────────────────
function PrismCorner({ size=80, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" style={{ position:"absolute", ...style }} aria-hidden="true">
      <polygon points="80,0 80,80 0,0"       fill={SBU.red}       opacity="0.15" />
      <polygon points="80,0 80,40 40,0"      fill={SBU.redBright} opacity="0.2"  />
      <polygon points="80,40 80,80 40,80"    fill={SBU.redDark}   opacity="0.12" />
    </svg>
  );
}

// ── Avatar helpers ─────────────────────────────────────────────────────────

// Simple hash from string → number (for deterministic colors/shapes per name)
function hashStr(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(31, h) + str.charCodeAt(i) | 0;
  }
  return Math.abs(h);
}

// MD5-lite via SubtleCrypto for Gravatar (async)
async function gravatarUrl(email, size) {
  const clean = email.trim().toLowerCase();
  const msgBuf = new TextEncoder().encode(clean);
  const hashBuf = await crypto.subtle.digest("SHA-256", msgBuf);
  const hex = Array.from(new Uint8Array(hashBuf))
    .map(b => b.toString(16).padStart(2,"0")).join("");
  // Gravatar uses MD5 but SHA-256 also works with their fallback=404
  // We use SHA-256 and d=404 so we can detect no photo
  return `https://www.gravatar.com/avatar/${hex}?s=${size}&d=404`;
}

// Animated geometric avatar (unique per person, no photo needed)
function GeometricAvatar({ name, size, tier }) {
  const h    = hashStr(name);
  const hues = [0, 8, 16, 200, 220, 260, 140]; // red palette + accents
  const hue1 = hues[h % hues.length];
  const hue2 = hues[(h + 3) % hues.length];
  const shapes = h % 4; // 0=triangles, 1=circles, 2=diamonds, 3=stripes

  const c1 = `hsl(${hue1},70%,${tier>=5?25:35}%)`;
  const c2 = `hsl(${hue2},60%,55%)`;
  const s  = size;
  const id = `av-${name.replace(/\s/g,"")}`;

  return (
    <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}
      style={{ borderRadius:"50%", flexShrink:0, boxShadow:`0 2px 8px rgba(0,0,0,0.2)` }}>
      <defs>
        <clipPath id={`clip-${id}`}>
          <circle cx={s/2} cy={s/2} r={s/2}/>
        </clipPath>
        <style>{`
          @keyframes ${id}-pulse {
            0%,100%{opacity:0.85} 50%{opacity:1}
          }
          @keyframes ${id}-spin {
            from{transform-origin:${s/2}px ${s/2}px;transform:rotate(0deg)}
            to{transform-origin:${s/2}px ${s/2}px;transform:rotate(360deg)}
          }
          .${id}-slow { animation:${id}-spin 12s linear infinite; }
          .${id}-pulse { animation:${id}-pulse 3s ease-in-out infinite; }
        `}</style>
      </defs>

      {/* Background */}
      <circle cx={s/2} cy={s/2} r={s/2} fill={c1}/>

      {/* Geometric pattern clipped to circle */}
      <g clipPath={`url(#clip-${id})`}>
        {shapes===0 && <>
          <polygon className={`${id}-slow`} points={`${s/2},${s*0.05} ${s*0.95},${s*0.78} ${s*0.05},${s*0.78}`} fill={c2} opacity="0.35"/>
          <polygon points={`${s/2},${s*0.22} ${s*0.82},${s*0.88} ${s*0.18},${s*0.88}`} fill="rgba(255,255,255,0.12)"/>
        </>}
        {shapes===1 && <>
          <circle className={`${id}-pulse`} cx={s*0.3} cy={s*0.35} r={s*0.28} fill={c2} opacity="0.4"/>
          <circle cx={s*0.72} cy={s*0.65} r={s*0.22} fill="rgba(255,255,255,0.15)"/>
        </>}
        {shapes===2 && <>
          <rect className={`${id}-slow`} x={s*0.15} y={s*0.15} width={s*0.7} height={s*0.7} fill={c2} opacity="0.35" transform={`rotate(45 ${s/2} ${s/2})`}/>
          <rect x={s*0.28} y={s*0.28} width={s*0.44} height={s*0.44} fill="rgba(255,255,255,0.12)" transform={`rotate(45 ${s/2} ${s/2})`}/>
        </>}
        {shapes===3 && <>
          {[0,1,2,3].map(i=>(
            <rect key={i} x={0} y={s*(i*0.25)} width={s} height={s*0.12} fill={i%2===0?c2:"rgba(255,255,255,0.1)"} opacity="0.45"/>
          ))}
        </>}
      </g>

      {/* Initials on top */}
      <text x={s/2} y={s/2} textAnchor="middle" dominantBaseline="central"
        fill="white" fontSize={s*0.33} fontWeight="700"
        fontFamily="'Source Serif 4',Georgia,serif"
        style={{ filter:"drop-shadow(0 1px 2px rgba(0,0,0,0.4))" }}>
        {getInitials(name)}
      </text>
    </svg>
  );
}

// ── Avatar (tries Gravatar, falls back to geometric) ────────────────────────
function Avatar({ name, email, size=48, tier=3 }) {
  const [photoUrl, setPhotoUrl] = useState(null); // null=loading, false=failed, str=url
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (!email) { setPhotoUrl(false); return; }
    setPhotoUrl(null);
    setImgError(false);
    gravatarUrl(email, size * 2).then(url => setPhotoUrl(url));
  }, [email, size]);

  // Show real photo if Gravatar found one
  if (photoUrl && !imgError) {
    return (
      <img
        src={photoUrl}
        alt={name}
        width={size} height={size}
        style={{ borderRadius:"50%", flexShrink:0, objectFit:"cover",
                 boxShadow:"0 2px 8px rgba(0,0,0,0.2)",
                 border:`2px solid rgba(255,255,255,0.15)` }}
        onError={() => setImgError(true)}
      />
    );
  }

  // Always show geometric while loading or on failure
  return <GeometricAvatar name={name} size={size} tier={tier} />;
}


function RankPill({ title }) {
  const r = TITLE_RANK[title] || { label:title, tier:1 };
  const colors = {
    6:{ bg:"#FFF3E0", text:"#CC6600", border:"#FFCC80" },
    5:{ bg:"#FFF3E0", text:"#CC6600", border:"#FFCC80" },
    4:{ bg:"#F3F0FF", text:"#5B21B6", border:"#C4B5FD" },
    3:{ bg:"#F0F7FF", text:"#1D4ED8", border:"#BFDBFE" },
    2:{ bg:"#F0FDF4", text:"#15803D", border:"#BBF7D0" },
    1:{ bg:"#F8F8F8", text:"#6B6262", border:"#E0DCDC" },
  };
  const c = colors[r.tier];
  return <span style={{ fontSize:10, padding:"2px 8px", borderRadius:3, background:c.bg, color:c.text, border:`1px solid ${c.border}`, fontFamily:"'Barlow Semi Condensed',sans-serif", fontWeight:600, letterSpacing:"0.03em", whiteSpace:"nowrap" }}>{r.label}</span>;
}

function DeptBadge({ dept }) {
  const meta = DEPT_META[dept] || { short:"?" };
  return <span style={{ fontSize:10, padding:"2px 8px", borderRadius:3, background:SBU.red, color:SBU.white, fontFamily:"'Alumni Sans',sans-serif", fontWeight:600, letterSpacing:"0.05em" }}>{meta.short}</span>;
}

function Spinner() {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:40 }}>
      <div style={{ width:28, height:28, borderRadius:"50%", border:`3px solid ${SBU.silver}`, borderTop:`3px solid ${SBU.red}`, animation:"spin 0.8s linear infinite" }} />
    </div>
  );
}

// ── Faculty Row ────────────────────────────────────────────────────────────
function FacultyRow({ f, isSelected, onClick }) {
  const [hov, setHov] = useState(false);
  const r = TITLE_RANK[f.title] || { tier:1 };
  return (
    <div onClick={onClick} onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ padding:"12px 16px", cursor:"pointer", borderBottom:`1px solid ${SBU.silver}`, background:isSelected?"#FFF5F7":hov?"#FFF9FA":SBU.white, borderLeft:`3px solid ${isSelected?SBU.red:"transparent"}`, transition:"all 0.15s" }}>
      <div style={{ display:"flex", gap:12, alignItems:"center" }}>
        <Avatar name={f.name} email={f.email} size={40} tier={r.tier} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:"'Bitter',Georgia,serif", fontSize:14, fontWeight:600, color:SBU.navy, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.name}</div>
          <div style={{ display:"flex", gap:5, marginTop:4, flexWrap:"wrap", alignItems:"center" }}>
            <DeptBadge dept={f.dept} />
            <RankPill title={f.title} />
          </div>
          {f.hIndex && (
            <div style={{ marginTop:4, fontFamily:"'Alumni Sans',sans-serif", fontSize:10, color:SBU.midgray }}>
              h-index <strong style={{ color:SBU.navy }}>{f.hIndex}</strong> · {f.citations?.toLocaleString()} citations
            </div>
          )}
        </div>
        {isSelected && <div style={{ color:SBU.red, fontSize:18 }}>›</div>}
      </div>
    </div>
  );
}

// ── Department Home ────────────────────────────────────────────────────────
function DeptHome({ onPick, deptStats, totalFaculty }) {
  return (
    <div style={{ flex:1, overflowY:"auto", background:SBU.lightgray }}>

      {/* Hero with SBU Prisms */}
      <div style={{ background:`linear-gradient(135deg, ${SBU.navy} 0%, #2A0010 60%, #1A000A 100%)`, padding:"52px 56px 44px", position:"relative", overflow:"hidden", minHeight:260 }}>

        {/* SBU Prism decoration — top right corner */}
        <PrismDecoration style={{ top:0, right:0, width:340, height:260, pointerEvents:"none" }} />

        {/* Thin red prism bar at very top — like SBU website */}
        <div style={{ position:"absolute", top:0, left:0, right:0, height:4, background:`linear-gradient(90deg, ${SBU.red} 0%, ${SBU.redBright} 50%, ${SBU.redDark} 100%)` }} />

        <div style={{ position:"relative", zIndex:2 }}>
          {/* BrookMinds wordmark */}
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
            <div style={{ display:"inline-flex", alignItems:"center", gap:2 }}>
              <span style={{ fontFamily:"'Bitter',Georgia,serif", fontSize:13, fontWeight:700, color:SBU.red, letterSpacing:"0.02em" }}>Brook</span>
              <span style={{ fontFamily:"'Barlow Semi Condensed',sans-serif", fontSize:13, fontWeight:300, color:"rgba(255,255,255,0.7)", letterSpacing:"0.08em" }}>MINDS</span>
            </div>
            <div style={{ width:1, height:14, background:"rgba(255,255,255,0.2)" }} />
            <span style={{ fontFamily:"'Alumni Sans',sans-serif", fontSize:10, color:"rgba(255,255,255,0.4)", letterSpacing:"0.1em" }}>STONY BROOK UNIVERSITY · CEAS</span>
          </div>

          <h1 style={{ fontFamily:"'Bitter',Georgia,serif", fontSize:38, fontWeight:700, color:SBU.white, margin:0, lineHeight:1.15, maxWidth:540 }}>
            Faculty Expert<br/>
            <span style={{ color:SBU.red }}>Finder</span>
          </h1>
          <p style={{ fontFamily:"'Barlow Semi Condensed',sans-serif", fontSize:15, color:"rgba(255,255,255,0.6)", marginTop:14, maxWidth:460, lineHeight:1.7 }}>
            Discover research expertise across the College of Engineering &amp; Applied Sciences. Connect with faculty for collaboration and innovation.
          </p>

          {/* Stats */}
          <div style={{ display:"flex", gap:32, marginTop:28 }}>
            {[
              { n:totalFaculty,    label:"Faculty"      },
              { n:9,               label:"Departments"  },
              { n:"2,700+",        label:"Inventions"   },
            ].map(({ n, label }) => (
              <div key={label}>
                <div style={{ fontFamily:"'Bitter',Georgia,serif", fontSize:28, fontWeight:700, color:SBU.white, lineHeight:1 }}>{n}</div>
                <div style={{ fontFamily:"'Alumni Sans',sans-serif", fontSize:10, color:"rgba(255,255,255,0.4)", marginTop:4, letterSpacing:"0.08em", textTransform:"uppercase" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Dept Grid */}
      <div style={{ padding:"32px 48px" }}>
        <div style={{ fontFamily:"'Barlow Semi Condensed',sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:SBU.midgray, marginBottom:16 }}>Browse by Department</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(220px,1fr))", gap:12 }}>
          {Object.entries(DEPT_META).map(([dept, meta]) => {
            const stat = deptStats.find(d=>d.name===dept) || {};
            return (
              <div key={dept} onClick={()=>onPick(dept)}
                style={{ background:SBU.white, borderRadius:6, border:`1px solid ${SBU.silver}`, padding:"18px 20px", cursor:"pointer", transition:"all 0.18s", borderTop:`3px solid ${SBU.red}`, position:"relative", overflow:"hidden" }}
                onMouseEnter={e=>{ e.currentTarget.style.boxShadow=`0 4px 18px rgba(204,0,51,0.13)`; e.currentTarget.style.transform="translateY(-2px)"; }}
                onMouseLeave={e=>{ e.currentTarget.style.boxShadow="none"; e.currentTarget.style.transform="translateY(0)"; }}>
                {/* Mini prism corner */}
                <PrismCorner size={60} style={{ top:0, right:0, opacity:0.6, pointerEvents:"none" }} />
                <div style={{ fontSize:24, marginBottom:10, position:"relative" }}>{meta.icon}</div>
                <div style={{ fontFamily:"'Bitter',Georgia,serif", fontSize:14, fontWeight:600, color:SBU.navy, lineHeight:1.3, marginBottom:10 }}>{dept}</div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontFamily:"'Barlow Semi Condensed',sans-serif", fontSize:11, color:SBU.midgray }}>
                    {stat.count||"—"} faculty{stat.avgHIndex?` · h=${stat.avgHIndex}`:""}
                  </span>
                  <span style={{ fontFamily:"'Alumni Sans',sans-serif", fontSize:10, fontWeight:600, background:SBU.red, color:SBU.white, padding:"2px 6px", borderRadius:2 }}>{meta.short}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Research Slide Tab ─────────────────────────────────────────────────────
function ResearchSlideTab({ faculty: f }) {
  const [showModal, setShowModal]   = useState(false);
  const [imgData,   setImgData]     = useState(null);
  const [imgName,   setImgName]     = useState("");
  const [extra, setExtra] = useState({
    focus:           f.researchFocus        || "",
    accomplishments: f.notableAchievements  || "",
    futureWork:      f.futureDirections     || "",
    applications:    f.industryApplications || "",
  });
  const [generated, setGenerated]   = useState(false);

  const [submitStatus, setSubmitStatus] = useState("idle"); // idle | saving | saved | error

  async function handleSaveToSheet() {
    setSubmitStatus("saving");
    try {
      const res = await fetch(`${API}/faculty/${f.id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          focus:           extra.focus,
          accomplishments: extra.accomplishments,
          futureWork:      extra.futureWork,
          applications:    extra.applications,
        }),
      });
      if (!res.ok) throw new Error(`Save failed: ${res.status}`);

      setSubmitStatus("saved");
      setTimeout(() => { setSubmitStatus("idle"); setShowModal(false); }, 1500);
    } catch (err) {
      setSubmitStatus("error");
      setTimeout(() => setSubmitStatus("idle"), 4000);
    }
  }

  useEffect(() => {
    if (f.researchSummary && !extra.focus) {
      setExtra(e => ({ ...e, focus: f.researchSummary }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [f.id]);

  const topics = (f.researchTopics || []).join(", ");

  function handleImage(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImgName(file.name);
    const reader = new FileReader();
    reader.onload = ev => setImgData(ev.target.result);
    reader.readAsDataURL(file);
  }

  function handleGenerate() {
    setGenerated(true);
    setShowModal(false);
  }

  function handlePrint() {
    const printWin = window.open('', '_blank');
    const slideHtml = document.getElementById('rs-slide-inner').outerHTML;
    printWin.document.write(`
      <html><head><title>${f.name} — Research Slide</title>
      <link href="https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@400;700&family=IBM+Plex+Sans:wght@300;400;600&family=IBM+Plex+Mono:wght@400;600&display=swap" rel="stylesheet">
      <style>
        * { box-sizing: border-box; margin:0; padding:0; }
        body { font-family: 'IBM Plex Sans', sans-serif; background: white; padding: 20px; }
        ${document.querySelector('style') ? '' : ''}
      </style></head><body>${slideHtml}</body></html>`);
    printWin.document.close();
    setTimeout(() => { printWin.print(); }, 800);
  }

  const sl = { // slide styles shorthand
    box: {
      background: "#F2F0F0", borderRadius: 6,
      borderLeft: `3px solid #CC0033`, padding: "14px 16px",
      flex: 1,
    },
    boxEmpty: {
      background: "#F2F0F0", borderRadius: 6,
      border: "2px dashed #E0DCDC", padding: "14px 16px",
      flex: 1, display:"flex", alignItems:"center", justifyContent:"center",
      textAlign:"center",
    },
    label: {
      fontFamily: "'IBM Plex Mono',monospace", fontSize: 9,
      fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em",
      color: "#CC0033", marginBottom: 8,
    },
    text: {
      fontFamily: "'IBM Plex Sans',sans-serif",
      fontSize: 12, color: "#3D3535", lineHeight: 1.65,
    },
    emptyTxt: {
      fontFamily: "'IBM Plex Sans',sans-serif",
      fontSize: 11, color: "#C0BAAA", fontStyle: "italic",
    },
  };

  return (
    <div style={{ padding: "24px 36px" }}>

      {/* Action bar */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <div>
          <div style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:15, fontWeight:700, color:"#221E1F" }}>Research Overview Slide</div>
          <div style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12, color:"#6B6262", marginTop:3 }}>
            Pre-filled from your profile. Add a diagram, accomplishments, and future work to complete it.
          </div>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={handlePrint}
            style={{ padding:"8px 16px", background:"#F2F0F0", border:"1.5px solid #E0DCDC", borderRadius:4, fontSize:12, fontFamily:"'IBM Plex Sans',sans-serif", fontWeight:600, color:"#6B6262", cursor:"pointer" }}>
            🖨 Print / PDF
          </button>
          <button onClick={() => setShowModal(true)}
            style={{ padding:"8px 16px", background:"#CC0033", border:"none", borderRadius:4, fontSize:12, fontFamily:"'IBM Plex Sans',sans-serif", fontWeight:600, color:"white", cursor:"pointer" }}>
            ✏️ Edit Slide
          </button>
        </div>
      </div>

      {/* ── THE SLIDE ── */}
      <div id="rs-slide-inner" style={{ border:"1px solid #E0DCDC", borderRadius:8, overflow:"hidden", boxShadow:"0 2px 16px rgba(0,0,0,0.08)" }}>

        {/* Slide header */}
        <div style={{ background:"linear-gradient(135deg, #221E1F 0%, #2A0010 100%)", padding:"20px 28px 16px", position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:"linear-gradient(90deg,#CC0033,#E8002D,#8C0023)" }} />
          <svg style={{ position:"absolute", top:0, right:0, width:180, height:100, pointerEvents:"none", opacity:0.7 }} viewBox="0 0 180 100">
            <polygon points="120,0 180,0 180,70" fill="#CC0033" opacity="0.9"/>
            <polygon points="180,70 180,100 140,100" fill="#8C0023" opacity="0.85"/>
            <polygon points="100,0 130,0 100,50" fill="#E8002D" opacity="0.6"/>
          </svg>
          <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:"rgba(255,255,255,0.3)", letterSpacing:"0.1em", marginBottom:8 }}>
            STONY BROOK UNIVERSITY · CEAS · BROOKMINDS
          </div>
          <div style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:20, fontWeight:700, color:"white", position:"relative", zIndex:1 }}>{f.name}</div>
          <div style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12, color:"rgba(255,255,255,0.55)", marginTop:5, position:"relative", zIndex:1 }}>
            {f.dept}{topics ? ` · ${topics}` : ""}
          </div>
        </div>

        {/* Slide 3-column grid */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", minHeight:360 }}>

          {/* Left */}
          <div style={{ padding:16, borderRight:"1px solid #E0DCDC", display:"flex", flexDirection:"column", gap:12 }}>
            <div style={sl.box}>
              <div style={sl.label}>Research Focus</div>
              <div style={sl.text}>
                {extra.focus || f.researchSummary || (
                  <span style={sl.emptyTxt}>Click "Edit Slide" to add research focus</span>
                )}
              </div>
            </div>
            <div style={extra.accomplishments ? sl.box : sl.boxEmpty}>
              {extra.accomplishments
                ? <><div style={sl.label}>Accomplishments</div><div style={sl.text}>{extra.accomplishments}</div></>
                : <span style={sl.emptyTxt}>Accomplishments<br/>Click Edit to add</span>
              }
            </div>
          </div>

          {/* Center */}
          <div style={{ padding:16, borderRight:"1px solid #E0DCDC", display:"flex", flexDirection:"column", gap:12 }}>
            <div style={{ ...sl.boxEmpty, flex:2, border:imgData?"none":"2px dashed #E0DCDC", padding:imgData?0:"14px", background:imgData?"transparent":"white", borderRadius:6, overflow:"hidden", minHeight:200 }}>
              {imgData
                ? <img src={imgData} alt="Research diagram" style={{ width:"100%", height:"100%", objectFit:"cover", borderRadius:6 }} />
                : <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100%" }}>
                    <div style={{ fontSize:28, opacity:0.3, marginBottom:8 }}>⚙️</div>
                    <span style={sl.emptyTxt}>Flow Diagram / Schematic<br/>Upload via Edit Slide</span>
                  </div>
              }
            </div>
            <div style={extra.futureWork ? sl.box : sl.boxEmpty}>
              {extra.futureWork
                ? <><div style={sl.label}>Further Exploration</div><div style={sl.text}>{extra.futureWork}</div></>
                : <span style={sl.emptyTxt}>Future work<br/>Click Edit to add</span>
              }
            </div>
          </div>

          {/* Right */}
          <div style={{ padding:16, display:"flex", flexDirection:"column", gap:12 }}>
            <div style={extra.applications ? sl.box : sl.boxEmpty}>
              {extra.applications
                ? <><div style={sl.label}>Other Potential Applications</div><div style={sl.text}>{extra.applications}</div></>
                : <span style={sl.emptyTxt}>Applications outside main area<br/>Click Edit to add</span>
              }
            </div>
            {/* Stats summary */}
            {f.hIndex && (
              <div style={{ ...sl.box, flex:0 }}>
                <div style={sl.label}>Research Impact</div>
                <div style={{ display:"flex", gap:16 }}>
                  {[["h-index", f.hIndex], ["Citations", f.citations?.toLocaleString()], ["Papers", f.worksCount]].map(([l,v]) => v ? (
                    <div key={l} style={{ textAlign:"center" }}>
                      <div style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:18, fontWeight:700, color:"#221E1F" }}>{v}</div>
                      <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:8, color:"#6B6262", textTransform:"uppercase" }}>{l}</div>
                    </div>
                  ) : null)}
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Slide footer */}
        <div style={{ background:"#221E1F", padding:"8px 20px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:"rgba(255,255,255,0.25)", letterSpacing:"0.05em" }}>
            BROOKMINDS · STONY BROOK UNIVERSITY CEAS
          </span>
          <span style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:9, color:"#CC0033", fontWeight:600 }}>
            brookminds.stonybrook.edu
          </span>
        </div>
      </div>

      {/* ── EDIT MODAL ── */}
      {showModal && (
        <div onClick={e => e.target===e.currentTarget && setShowModal(false)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
          <div style={{ background:"white", borderRadius:8, width:"100%", maxWidth:520, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }}>

            {/* Modal header */}
            <div style={{ background:"linear-gradient(135deg,#221E1F,#2A0010)", padding:"20px 24px", borderRadius:"8px 8px 0 0", position:"relative" }}>
              <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:"linear-gradient(90deg,#CC0033,#E8002D,#8C0023)" }} />
              <div style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:16, fontWeight:700, color:"white" }}>Edit Research Slide</div>
              <div style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12, color:"rgba(255,255,255,0.5)", marginTop:3 }}>Add details to complete your slide</div>
              <button onClick={() => setShowModal(false)}
                style={{ position:"absolute", top:16, right:16, background:"rgba(255,255,255,0.1)", border:"none", color:"white", borderRadius:4, width:28, height:28, cursor:"pointer", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
            </div>

            {/* Modal body */}
            <div style={{ padding:"20px 24px" }}>

              {/* Pre-filled read-only fields */}
              <div style={{ marginBottom:16, padding:"12px 14px", background:"#F2F0F0", borderRadius:6, borderLeft:"3px solid #CC0033" }}>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:"#CC0033", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:6 }}>Auto-filled from your profile</div>
                <div style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12, color:"#3D3535" }}><strong>{f.name}</strong> · {f.dept}</div>
                {topics && <div style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:11, color:"#6B6262", marginTop:3 }}>{topics}</div>}
              </div>

              {[
                ["Research Focus / Problems Solved", "focus", "Describe your core research focus and what problems you're solving...", 3],
                ["Accomplishments", "accomplishments", "Grants received, papers published, systems built, awards...", 3],
                ["Future Work / Topics to Explore", "futureWork", "What directions are you pursuing next?", 3],
                ["Applications Outside Main Area", "applications", "Other domains your technology or methods could be applied to...", 3],
              ].map(([label, key, placeholder, rows]) => (
                <div key={key} style={{ marginBottom:16 }}>
                  <label style={{ display:"block", fontFamily:"'IBM Plex Mono',monospace", fontSize:9, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:"#6B6262", marginBottom:6 }}>
                    {label}
                  </label>
                  <textarea
                    value={extra[key]}
                    onChange={e => setExtra(prev => ({ ...prev, [key]: e.target.value }))}
                    placeholder={placeholder}
                    rows={rows}
                    style={{ width:"100%", padding:"10px 12px", border:"1.5px solid #E0DCDC", borderRadius:4, fontFamily:"'IBM Plex Sans',sans-serif", fontSize:13, color:"#221E1F", outline:"none", resize:"vertical" }}
                    onFocus={e => e.target.style.borderColor="#CC0033"}
                    onBlur={e => e.target.style.borderColor="#E0DCDC"}
                  />
                </div>
              ))}

              {/* Image upload */}
              <div style={{ marginBottom:20 }}>
                <label style={{ display:"block", fontFamily:"'IBM Plex Mono',monospace", fontSize:9, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:"#6B6262", marginBottom:6 }}>
                  Flow Diagram / Schematic Image
                </label>
                <div style={{ border:"2px dashed #E0DCDC", borderRadius:4, padding:"16px", textAlign:"center", position:"relative", cursor:"pointer", transition:"all 0.15s", background:imgData?"#FFF5F7":"white" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor="#CC0033"}
                  onMouseLeave={e => e.currentTarget.style.borderColor=imgData?"#CC0033":"#E0DCDC"}>
                  <input type="file" accept="image/*" onChange={handleImage}
                    style={{ position:"absolute", inset:0, opacity:0, cursor:"pointer", width:"100%", height:"100%" }} />
                  <div style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12, color:"#6B6262" }}>
                    {imgName
                      ? <span style={{ color:"#CC0033", fontWeight:600 }}>✓ {imgName}</span>
                      : <>Drop image here or <span style={{ color:"#CC0033", fontWeight:600 }}>browse</span><br/><span style={{ fontSize:11, color:"#AAA" }}>PNG, JPG, SVG — flow diagrams, device schematics</span></>
                    }
                  </div>
                </div>
              </div>

              <button onClick={handleGenerate}
                style={{ width:"100%", padding:"13px", background:"#CC0033", border:"none", borderRadius:4, fontFamily:"'IBM Plex Sans',sans-serif", fontSize:13, fontWeight:600, color:"white", cursor:"pointer", letterSpacing:"0.04em", marginBottom:10 }}>
                Update Slide →
              </button>

              {/* Save to database */}
              <button
                onClick={async () => { setGenerated(true); await handleSaveToSheet(); }}
                disabled={submitStatus === "saving"}
                style={{
                  width:"100%", padding:"13px",
                  background: submitStatus === "saved"  ? "#15803D"
                            : submitStatus === "error"  ? "#92400E"
                            : submitStatus === "saving" ? "#6B6262"
                            : "#221E1F",
                  border:"none", borderRadius:4,
                  fontFamily:"'IBM Plex Sans',sans-serif", fontSize:13, fontWeight:600,
                  color:"white", cursor: submitStatus === "saving" ? "wait" : "pointer",
                  letterSpacing:"0.04em", display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                  transition:"background 0.2s",
                }}>
                {submitStatus === "saving" ? "⏳ Saving..."
               : submitStatus === "saved"  ? "✓ Saved!"
               : submitStatus === "error"  ? "✗ Error — try again"
               : "📊 Update Slide + Save"}
              </button>
              <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:"#AAA", textAlign:"center", marginTop:6 }}>
                Saves your details to the BrookMinds research database
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Profile Panel ──────────────────────────────────────────────────────────
function Profile({ faculty }) {
  const [tab, setTab]         = useState("overview");
  const [headerH, setHeaderH] = useState(null); // null = auto
  const dragRef               = useRef(null);
  useEffect(()=>{ setTab("overview"); setHeaderH(null); }, [faculty?.id]);

  if (!faculty) return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", background:SBU.lightgray, gap:12 }}>
      <div style={{ fontSize:52, opacity:0.2 }}>🎓</div>
      <div style={{ fontFamily:"'Bitter',Georgia,serif", fontSize:18, color:SBU.midgray }}>Select a faculty member</div>
      <div style={{ fontFamily:"'Barlow Semi Condensed',sans-serif", fontSize:13, color:"#AAA" }}>to view their full profile</div>
    </div>
  );

  const f    = faculty;
  const r    = TITLE_RANK[f.title] || { tier:1 };
  const meta = DEPT_META[f.dept] || { short:"?", icon:"🔬" };
  const scholarUrl = f.scholarUrl || `https://scholar.google.com/scholar?q=${encodeURIComponent(f.name+" Stony Brook")}`;

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", background:SBU.white, overflow:"hidden" }}>

      {/* Header — drag-resizable */}
      <div style={{ background:`linear-gradient(135deg, ${SBU.navy} 0%, #2A0010 100%)`, height: headerH ?? "auto", minHeight:48, maxHeight:"80%", position:"relative", overflow:"hidden", flexShrink:0 }}>
        <PrismDecoration style={{ top:0, right:0, width:280, height:160, pointerEvents:"none", opacity:0.7 }} />
        <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg,${SBU.red},${SBU.redBright},${SBU.redDark})` }} />

        <div style={{ padding:"28px 36px 24px", position:"relative", zIndex:2, height:"100%", overflow:"hidden" }}>
          <div style={{ display:"flex", gap:20, alignItems:"flex-start" }}>
            <Avatar name={f.name} email={f.email} size={64} tier={r.tier} />
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:26, fontWeight:700, color:SBU.white, lineHeight:1.2 }}>{f.name}</div>
              <div style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:13, color:"rgba(255,255,255,0.55)", marginTop:5 }}>{f.dept}</div>
              <div style={{ display:"flex", gap:8, marginTop:10, flexWrap:"wrap", alignItems:"center" }}>
                <RankPill title={f.title} />
                <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:"rgba(255,255,255,0.45)", background:"rgba(255,255,255,0.08)", padding:"2px 8px", borderRadius:3 }}>{meta.short} {meta.icon}</span>
                {f.scraped && <span style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:10, color:"#4ADE80", background:"rgba(74,222,128,0.15)", border:"1px solid rgba(74,222,128,0.3)", padding:"2px 8px", borderRadius:3 }}>✓ Live Data</span>}
              </div>
              {f.researchSummary && (
                <div style={{ marginTop:14, paddingTop:14, borderTop:"1px solid rgba(255,255,255,0.1)" }}>
                  <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:"rgba(255,255,255,0.35)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:6 }}>Research Summary</div>
                  <p style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:13, color:"rgba(255,255,255,0.75)", lineHeight:1.7, margin:0, maxWidth:560 }}>{f.researchSummary}</p>
                </div>
              )}
            </div>
            <a href={scholarUrl} target="_blank" rel="noopener noreferrer"
              style={{ flexShrink:0, padding:"8px 16px", background:SBU.red, color:SBU.white, borderRadius:4, fontSize:12, textDecoration:"none", fontFamily:"'IBM Plex Sans',sans-serif", fontWeight:600 }}
              onMouseEnter={e=>e.currentTarget.style.background=SBU.redDark}
              onMouseLeave={e=>e.currentTarget.style.background=SBU.red}>
              Google Scholar ↗
            </a>
          </div>
        </div>

        {/* ── Drag handle ── */}
        <div
          ref={dragRef}
          title="Drag to resize"
          onMouseDown={e => {
            e.preventDefault();
            const startY     = e.clientY;
            const startH     = dragRef.current.parentElement.getBoundingClientRect().height;
            const onMove = mv => {
              const newH = Math.max(48, Math.min(startH + (mv.clientY - startY), window.innerHeight * 0.8));
              setHeaderH(newH);
            };
            const onUp = () => {
              window.removeEventListener("mousemove", onMove);
              window.removeEventListener("mouseup",   onUp);
            };
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup",   onUp);
          }}
          style={{
            position:"absolute", bottom:0, left:0, right:0,
            height:14, cursor:"ns-resize",
            display:"flex", alignItems:"center", justifyContent:"center",
            background:"linear-gradient(to top, rgba(0,0,0,0.25), transparent)",
            zIndex:10, userSelect:"none",
          }}>
          {/* Visual grip dots */}
          <div style={{ display:"flex", gap:4, alignItems:"center" }}>
            {[0,1,2,3,4].map(i=>(
              <div key={i} style={{ width:4, height:4, borderRadius:"50%", background:"rgba(255,255,255,0.35)" }} />
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom:`2px solid ${SBU.silver}`, background:SBU.white, flexShrink:0 }}>
        <div style={{ display:"flex", padding:"0 36px" }}>
          {["overview","publications","contact","research slide"].map(t=>(
            <button key={t} onClick={()=>setTab(t)} style={{ padding:"14px 20px", border:"none", cursor:"pointer", background:"transparent", fontFamily:"'Barlow Semi Condensed',sans-serif", fontSize:13, fontWeight:600, textTransform:"capitalize", color:tab===t?SBU.red:SBU.midgray, borderBottom:tab===t?`2px solid ${SBU.red}`:"2px solid transparent", marginBottom:-2, transition:"color 0.15s", whiteSpace:"nowrap" }}>{t}</button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex:1, overflowY:"auto", padding: tab==="research slide" ? "0" : "28px 36px" }}>

        {tab==="overview" && (
          <div>
            {/* Data source badge */}
            {f.dataSource === "supabase" && (
              <div style={{ marginBottom:16, padding:"8px 14px", background:"#F0FDF4", border:"1px solid #BBF7D0", borderRadius:6, display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:11, color:"#15803D", fontFamily:"'IBM Plex Mono',monospace", fontWeight:600 }}>✦ AI-ENRICHED PROFILE</span>
                <span style={{ fontSize:10, color:"#6B6262", fontFamily:"'IBM Plex Sans',sans-serif" }}>Data from Google Scholar + Semantic Scholar + OpenAlex · Extracted by GPT-4o-mini</span>
              </div>
            )}

            {/* Elevator Pitch — most prominent new field */}
            {f.elevatorPitch && (
              <div style={{ marginBottom:20, padding:"16px 18px", background:`linear-gradient(135deg, #FFF5F7, #FFF0F3)`, border:`1px solid #FFB3C1`, borderRadius:6, borderLeft:`4px solid ${SBU.red}` }}>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:SBU.red, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:8 }}>💡 Elevator Pitch for Industry</div>
                <p style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:13, color:SBU.navy, lineHeight:1.7, margin:0 }}>{f.elevatorPitch}</p>
              </div>
            )}

            {/* Problem Statement */}
            {f.problemStatement && (
              <div style={{ marginBottom:20, padding:"14px 16px", background:SBU.lightgray, borderRadius:6, borderLeft:`3px solid ${SBU.red}` }}>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:SBU.midgray, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:6 }}>Problem Being Solved</div>
                <p style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:13, color:SBU.navy, lineHeight:1.65, margin:0 }}>{f.problemStatement}</p>
              </div>
            )}

            {/* Stats */}
            <div style={{ display:"flex", gap:12, marginBottom:24 }}>
              {[["h-index",f.hIndex],["Citations",f.citations],["Publications",f.worksCount]].map(([label,val])=>(
                <div key={label} style={{ flex:1, textAlign:"center", padding:"16px 10px", border:`1px solid ${SBU.silver}`, borderRadius:6, background:SBU.white, position:"relative", overflow:"hidden" }}>
                  <PrismCorner size={40} style={{ top:0, right:0, opacity:0.4 }} />
                  <div style={{ fontFamily:"'Bitter',Georgia,serif", fontSize:24, fontWeight:700, color:val?SBU.navy:SBU.silver }}>{val?val.toLocaleString():"—"}</div>
                  <div style={{ fontFamily:"'Alumni Sans',sans-serif", fontSize:9, color:SBU.midgray, textTransform:"uppercase", letterSpacing:"0.08em", marginTop:4 }}>{label}</div>
                  <div style={{ fontFamily:"'Barlow Semi Condensed',sans-serif", fontSize:9, color:"#C0BAAA", marginTop:2 }}>{val?"OpenAlex":"Pending"}</div>
                </div>
              ))}

            </div>

            {/* Key Methods */}
            {f.keyMethods?.length > 0 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, textTransform:"uppercase", letterSpacing:"0.1em", color:SBU.midgray, marginBottom:10 }}>Key Methods & Techniques</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {f.keyMethods.map(m=>(
                    <span key={m} style={{ padding:"4px 10px", borderRadius:4, background:"#EFF6FF", border:"1px solid #BFDBFE", color:"#1D4ED8", fontSize:11, fontFamily:"'IBM Plex Mono',monospace" }}>{m}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Research Topics / Keywords */}
            {f.researchTopics?.length > 0 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, textTransform:"uppercase", letterSpacing:"0.1em", color:SBU.midgray, marginBottom:10 }}>Research Keywords</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {f.researchTopics.map(topic=>(
                    <span key={topic} style={{ padding:"4px 10px", borderRadius:4, background:"#FFF5F7", border:`1px solid #FFB3C1`, color:SBU.red, fontSize:11, fontFamily:"'IBM Plex Sans',sans-serif", fontWeight:500 }}>{topic}</span>
                  ))}
                </div>
              </div>
            )}









            {/* Notable achievements + Lab name */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:20 }}>
              {[["Department",f.dept],["Academic Title",f.title]].map(([label,val])=>(
                <div key={label} style={{ padding:"16px 18px", background:SBU.lightgray, borderRadius:6, borderLeft:`3px solid ${SBU.red}`, position:"relative", overflow:"hidden" }}>
                  <PrismCorner size={50} style={{ top:0, right:0, opacity:0.3 }} />
                  <div style={{ fontFamily:"'Alumni Sans',sans-serif", fontSize:9, textTransform:"uppercase", letterSpacing:"0.1em", color:SBU.midgray, marginBottom:6 }}>{label}</div>
                  <div style={{ fontFamily:"'Barlow Semi Condensed',sans-serif", fontSize:13, color:SBU.navy, lineHeight:1.4 }}>{val}</div>
                </div>
              ))}
            </div>
            {(f.labName || f.notableAchievements) && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:20 }}>
                {f.labName && (
                  <div style={{ padding:"14px 16px", background:SBU.lightgray, borderRadius:6, borderLeft:`3px solid ${SBU.red}` }}>
                    <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:SBU.midgray, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:6 }}>Lab / Research Group</div>
                    <div style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:13, color:SBU.navy }}>{f.labName}</div>
                  </div>
                )}
                {f.notableAchievements && (
                  <div style={{ padding:"14px 16px", background:SBU.lightgray, borderRadius:6, borderLeft:`3px solid ${SBU.red}` }}>
                    <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:SBU.midgray, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:6 }}>Notable Achievements</div>
                    <div style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12, color:SBU.navy, lineHeight:1.5 }}>{f.notableAchievements}</div>
                  </div>
                )}
              </div>
            )}

            {!f.scraped && (
              <div style={{ background:"#FFF5F7", border:`1px solid #FFB3C1`, borderRadius:6, padding:"16px 20px" }}>
                <div style={{ fontFamily:"'Barlow Semi Condensed',sans-serif", fontSize:13, color:SBU.charcoal, lineHeight:1.7 }}>
                  📚 Research data not found in OpenAlex.
                  <a href={scholarUrl} target="_blank" rel="noopener noreferrer" style={{ display:"block", marginTop:8, color:SBU.red, fontWeight:600, textDecoration:"none" }}>Search on Google Scholar →</a>
                </div>
              </div>
            )}
          </div>
        )}

        {tab==="publications" && (() => {
          const impactful = Array.isArray(f.impactfulPapers)
            ? f.impactfulPapers
            : (() => { try { return JSON.parse(f.impactfulPapers||"[]"); } catch{ return []; } })();
          const allPubs   = f.recentPubs || [];

          // Merge: impactful first (with why_impactful), then rest sorted by citations
          const impactfulTitles = new Set(impactful.map(p=>p.title));
          const rest = allPubs
            .filter(p => !impactfulTitles.has(p.title))
            .sort((a,b) => (b.citations||0) - (a.citations||0));

          const PubCard = ({ pub, isImpactful }) => {
            const [expanded, setExpanded] = useState(false);
            const paperUrl = pub.url || (pub.doi
              ? (pub.doi.startsWith("http") ? pub.doi : `https://doi.org/${pub.doi}`)
              : `https://scholar.google.com/scholar?q=${encodeURIComponent(pub.title)}`);

            return (
              <div style={{ padding:"16px 0", borderBottom:`1px solid ${SBU.silver}` }}>
                {/* Title — clickable */}
                <a href={paperUrl} target="_blank" rel="noopener noreferrer"
                  style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:14, fontWeight:600, color:"#1a0dab", lineHeight:1.5, textDecoration:"none", display:"block", marginBottom:4 }}
                  onMouseEnter={e=>e.currentTarget.style.textDecoration="underline"}
                  onMouseLeave={e=>e.currentTarget.style.textDecoration="none"}>
                  {isImpactful && <span style={{ fontSize:11, color:"#F59E0B", marginRight:6 }}>★</span>}
                  {pub.title}
                </a>

                {/* Authors */}
                {pub.authors?.length > 0 && (
                  <div style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12, color:"#006621", marginBottom:3 }}>
                    {pub.authors.slice(0,5).join(", ")}{pub.authors.length > 5 ? ", ..." : ""}
                  </div>
                )}

                {/* Journal + Year + Citations */}
                <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap", marginBottom:4 }}>
                  {pub.journal && (
                    <span style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12, color:SBU.midgray, fontStyle:"italic" }}>
                      {pub.journal}
                    </span>
                  )}
                  {pub.year && (
                    <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:11, color:SBU.midgray }}>
                      {pub.year}
                    </span>
                  )}
                  {pub.citations > 0 && (
                    <a href={`https://scholar.google.com/scholar?cites=${encodeURIComponent(pub.title)}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12, color:"#1a0dab", textDecoration:"none" }}
                      onMouseEnter={e=>e.currentTarget.style.textDecoration="underline"}
                      onMouseLeave={e=>e.currentTarget.style.textDecoration="none"}>
                      Cited by {pub.citations}
                    </a>
                  )}
                  {pub.doi && (
                    <a href={pub.doi.startsWith("http") ? pub.doi : `https://doi.org/${pub.doi}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:SBU.red, textDecoration:"none", padding:"1px 6px", border:`1px solid ${SBU.red}`, borderRadius:3 }}>
                      DOI ↗
                    </a>
                  )}
                </div>

                {/* Abstract snippet — expandable */}
                {pub.abstract && (
                  <div>
                    <p style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12, color:SBU.charcoal, lineHeight:1.6, margin:"4px 0", maxHeight: expanded?"none":"3.6em", overflow:"hidden" }}>
                      {pub.abstract}
                    </p>
                    <button onClick={()=>setExpanded(e=>!e)}
                      style={{ background:"none", border:"none", color:SBU.red, fontSize:11, fontFamily:"'IBM Plex Sans',sans-serif", cursor:"pointer", padding:0, marginTop:2 }}>
                      {expanded ? "▲ Show less" : "▼ Show abstract"}
                    </button>
                  </div>
                )}

                {/* Why impactful */}
                {isImpactful && pub.why_impactful && (
                  <div style={{ marginTop:6, padding:"6px 10px", background:"#FFFBEB", borderRadius:4, borderLeft:"3px solid #F59E0B" }}>
                    <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:"#92400E", textTransform:"uppercase", letterSpacing:"0.06em" }}>Why impactful · </span>
                    <span style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12, color:"#78350F", fontStyle:"italic" }}>{pub.why_impactful}</span>
                  </div>
                )}
              </div>
            );
          };

          return (
            <div>
              {/* Stats bar */}
              <div style={{ display:"flex", gap:20, marginBottom:20, padding:"12px 16px", background:SBU.lightgray, borderRadius:6 }}>
                <div>
                  <div style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:20, fontWeight:700, color:SBU.navy }}>{f.worksCount||"—"}</div>
                  <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:SBU.midgray, textTransform:"uppercase" }}>Publications</div>
                </div>
                <div>
                  <div style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:20, fontWeight:700, color:SBU.navy }}>{f.citations?.toLocaleString()||"—"}</div>
                  <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:SBU.midgray, textTransform:"uppercase" }}>Citations</div>
                </div>
                <div>
                  <div style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:20, fontWeight:700, color:SBU.navy }}>{f.hIndex||"—"}</div>
                  <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:SBU.midgray, textTransform:"uppercase" }}>h-index</div>
                </div>
                <div style={{ marginLeft:"auto", display:"flex", alignItems:"center" }}>
                  <a href={scholarUrl} target="_blank" rel="noopener noreferrer"
                    style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12, fontWeight:600, color:SBU.red, textDecoration:"none", padding:"6px 12px", border:`1.5px solid ${SBU.red}`, borderRadius:4 }}>
                    View all on Google Scholar ↗
                  </a>
                </div>
              </div>

              {/* Most cited papers */}
              {impactful.length > 0 && (
                <div style={{ marginBottom:8 }}>
                  <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, textTransform:"uppercase", letterSpacing:"0.1em", color:"#92400E", marginBottom:4 }}>
                    ★ Most cited papers
                  </div>
                  {impactful.map((pub,i) => <PubCard key={i} pub={pub} isImpactful={true} />)}
                </div>
              )}

              {/* All other papers */}
              {rest.length > 0 && (
                <div>
                  {impactful.length > 0 && (
                    <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, textTransform:"uppercase", letterSpacing:"0.1em", color:SBU.midgray, margin:"16px 0 4px" }}>
                      All publications
                    </div>
                  )}
                  {rest.map((pub,i) => <PubCard key={i} pub={pub} isImpactful={false} />)}
                </div>
              )}

              {allPubs.length === 0 && (
                <div style={{ padding:32, textAlign:"center", color:SBU.midgray, fontFamily:"'IBM Plex Sans',sans-serif", fontSize:13 }}>
                  No publication data available.
                  <br/><a href={scholarUrl} target="_blank" rel="noopener noreferrer" style={{ color:SBU.red, marginTop:8, display:"inline-block" }}>Search on Google Scholar →</a>
                </div>
              )}
            </div>
          );
        })()}

        {tab==="research slide" && <ResearchSlideTab faculty={f} />}

        {tab==="contact" && (
          <div>
            <div style={{ background:SBU.white, border:`1px solid ${SBU.silver}`, borderRadius:6, overflow:"hidden" }}>
              {[
                ["Department", f.dept],
                ["University", "Stony Brook University, Stony Brook, NY 11794"],
                ["College", "College of Engineering & Applied Sciences"],
              ].map(([label,val],i,arr)=>(
                <div key={label} style={{ padding:"16px 20px", borderBottom:i<arr.length-1?`1px solid ${SBU.silver}`:"none", display:"flex", gap:16, position:"relative", overflow:"hidden" }}>
                  <PrismCorner size={40} style={{ top:0, right:0, opacity:0.15 }} />
                  <div style={{ width:120, flexShrink:0, fontFamily:"'Alumni Sans',sans-serif", fontSize:10, textTransform:"uppercase", letterSpacing:"0.1em", color:SBU.midgray, paddingTop:2 }}>{label}</div>
                  <div style={{ fontFamily:"'Barlow Semi Condensed',sans-serif", fontSize:13, color:SBU.navy, lineHeight:1.5 }}>{val}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:14, padding:"16px 18px", background:SBU.lightgray, borderRadius:6, borderLeft:`3px solid ${SBU.red}` }}>
              <div style={{ fontFamily:"'Barlow Semi Condensed',sans-serif", fontSize:13, color:SBU.charcoal, lineHeight:1.7 }}>
                <strong style={{ color:SBU.navy }}>Interested in collaborating?</strong>{" "}
                Reach out via email or visit the{" "}
                <a href="https://www.stonybrook.edu/ceas/" target="_blank" rel="noopener noreferrer" style={{ color:SBU.red }}>CEAS website</a>.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Keyword search (client-side, instant, no API call) ─────────────────────
function keywordFilter(faculty, q, dept) {
  let r = faculty;
  if (dept !== "All") r = r.filter(f => f.dept === dept);
  if (!q.trim()) return r;
  const ql = q.toLowerCase();
  const qlName = ql.replace(/[.,]/g, "").replace(/\s+/g, " ").trim();
  return r.map(f => {
    let s = 0;
    const nameNorm = f.name.toLowerCase().replace(/[.,]/g, "").replace(/\s+/g, " ").trim();
    if (nameNorm.includes(qlName))                                          s += 10;
    if (f.dept.toLowerCase().includes(ql))                                  s += 6;
    if (f.title.toLowerCase().includes(ql))                                 s += 3;
    if ((f.researchTopics||[]).join(" ").toLowerCase().includes(ql))        s += 5;
    if ((f.recentPubs||[]).map(p=>p.title).join(" ").toLowerCase().includes(ql)) s += 2;
    return { ...f, _s: s };
  }).filter(f => f._s > 0).sort((a, b) => b._s - a._s);
}

// ── Detect if query is "conceptual" (warrants semantic search) ─────────────
// Rule: if query is 3+ words OR contains no exact faculty name/dept match,
// treat it as a semantic search candidate.
function looksConceptual(q, faculty) {
  const ql = q.toLowerCase().trim();
  if (ql.split(" ").length >= 3) return true;
  // Check if any faculty name or dept exactly starts with the query
  const exactHit = faculty.some(f =>
    f.name.toLowerCase().startsWith(ql) ||
    f.dept.toLowerCase().startsWith(ql)
  );
  return !exactHit;
}

// ── Search mode badge ──────────────────────────────────────────────────────
function SearchBadge({ mode, count, suppressed }) {
  if (mode === "semantic") return (
    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
      <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, background:"#0F172A", color:"#4ADE80", padding:"2px 7px", borderRadius:3, letterSpacing:"0.05em" }}>
        ✦ AI SEARCH
      </span>
      <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:SBU.midgray }}>
        {count} match{count!==1?"es":""}
        {suppressed > 0 ? ` · ${suppressed} suppressed` : ""}
      </span>
    </div>
  );
  if (mode === "keyword") return (
    <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:SBU.midgray }}>
      {count} result{count!==1?"s":""}
    </span>
  );
  return null;
}

// ── Semantic result card — shows match confidence + reason ─────────────────
function SemanticResultCard({ result, isSelected, onClick }) {
  const [hov, setHov] = useState(false);
  const f = result.faculty || result;
  const r = TITLE_RANK[f.title] || { tier: 1 };
  const isHigh = result.confidence === "HIGH";

  return (
    <div onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ padding:"12px 16px", cursor:"pointer", borderBottom:`1px solid ${SBU.silver}`, background:isSelected?"#FFF5F7":hov?"#FFF9FA":SBU.white, borderLeft:`3px solid ${isSelected?SBU.red:isHigh?"#4ADE80":"#FACC15"}`, transition:"all 0.15s" }}>
      <div style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
        <Avatar name={f.name} email={f.email} size={40} tier={r.tier} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:14, fontWeight:600, color:SBU.navy, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.name}</div>
          <div style={{ display:"flex", gap:5, marginTop:4, flexWrap:"wrap", alignItems:"center" }}>
            <DeptBadge dept={f.dept} />
            <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:isHigh?"#15803D":"#92400E", background:isHigh?"#F0FDF4":"#FFFBEB", border:`1px solid ${isHigh?"#BBF7D0":"#FDE68A"}`, padding:"1px 6px", borderRadius:2 }}>
              {isHigh ? "●" : "◐"} {result.confidence}
            </span>
          </div>
          {result.match_reason && (
            <div style={{ marginTop:5, fontFamily:"'IBM Plex Sans',sans-serif", fontSize:10, color:SBU.midgray, lineHeight:1.4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {result.match_reason}
            </div>
          )}
          {f.hIndex && (
            <div style={{ marginTop:3, fontFamily:"'IBM Plex Mono',monospace", fontSize:9, color:SBU.midgray }}>
              h={f.hIndex} · {f.citations?.toLocaleString()} citations
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── No results panel ───────────────────────────────────────────────────────
function NoResults({ q, mode }) {
  return (
    <div style={{ padding:"32px 20px", textAlign:"center" }}>
      <div style={{ fontSize:32, marginBottom:12 }}>🔍</div>
      <div style={{ fontFamily:"'Source Serif 4',Georgia,serif", fontSize:15, color:SBU.navy, marginBottom:8 }}>
        No {mode==="semantic" ? "semantic matches" : "results"} found
      </div>
      <div style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12, color:SBU.midgray, lineHeight:1.7, maxWidth:220, margin:"0 auto" }}>
        {mode === "semantic"
          ? <>The AI found no faculty whose research closely matches <strong>"{q}"</strong>. Try a more specific term or rephrase.</>
          : <>No faculty matched <strong>"{q}"</strong>. Try a different keyword.</>
        }
      </div>
    </div>
  );
}

// ── Main App ───────────────────────────────────────────────────────────────
export default function App() {
  const [faculty, setFaculty]         = useState([]);
  const [deptStats, setDeptStats]     = useState([]);
  const [sel, setSel]                 = useState(null);
  const [q, setQ]                     = useState("");
  const [dept, setDept]               = useState("All");
  const [view, setView]               = useState("home");
  const [loading, setLoading]         = useState(true);
  const [apiError, setApiError]       = useState(false);

  // Semantic search state
  const [searchMode, setSearchMode]   = useState("keyword"); // "keyword" | "semantic" | "searching"
  const [semanticResults, setSemanticResults] = useState(null); // null = not run yet
  const [suppressed, setSuppressed]   = useState(0);

  const debouncedQ = useDebounce(q, 600); // slightly longer debounce for semantic

  // Load all faculty on mount
  useEffect(() => {
    fetch(`${API}/faculty`)
      .then(r => r.json()).then(d => { setFaculty(d); setLoading(false); })
      .catch(() => { setApiError(true); setLoading(false); });
    fetch(`${API}/departments`).then(r => r.json()).then(setDeptStats).catch(() => {});
  }, []);

  // Decide search mode and run search when debounced query changes
  useEffect(() => {
    if (!debouncedQ.trim()) {
      setSearchMode("keyword");
      setSemanticResults(null);
      return;
    }

    // Short queries (1-2 words) that match a name/dept → pure keyword, instant
    if (!looksConceptual(debouncedQ, faculty)) {
      setSearchMode("keyword");
      setSemanticResults(null);
      return;
    }

    // Conceptual query → hit the semantic endpoint
    setSearchMode("searching");
    const deptParam = dept !== "All" ? `&dept=${encodeURIComponent(dept)}` : "";
    fetch(`${API}/semantic-search?q=${encodeURIComponent(debouncedQ)}${deptParam}`)
      .then(r => r.json())
      .then(data => {
        if (data.mode === "keyword_fallback") {
          // Embeddings not built yet — silently fall back
          setSearchMode("keyword");
          setSemanticResults(null);
        } else {
          setSearchMode("semantic");
          setSemanticResults(data.results || []);
          setSuppressed(data.suppressed || 0);
        }
      })
      .catch(() => {
        // Network error — fall back to keyword gracefully
        setSearchMode("keyword");
        setSemanticResults(null);
      });
  }, [debouncedQ, dept, faculty]);

  // What actually shows in the sidebar list
  const displayed = (() => {
    if (searchMode === "semantic" && semanticResults !== null) {
      return semanticResults; // These are {faculty, score, confidence, match_reason} objects
    }
    return keywordFilter(faculty, q, dept);
  })();

  const pickDept = d => { setDept(d); setView("list"); setQ(""); setSel(null); setSemanticResults(null); setSearchMode("keyword"); };
  const doSearch = v => { setQ(v); setSel(null); if (v.trim()) { setView("list"); setDept("All"); } };
  const reset    = () => { setQ(""); setDept("All"); setSel(null); setView("home"); setSemanticResults(null); setSearchMode("keyword"); };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Alumni+Sans:wght@300;400;600;700;800&family=Bitter:wght@400;500;600;700&family=Barlow+Semi+Condensed:wght@300;400;500;600;700&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        html,body{height:100%;}
        body{background:#F2F0F0;font-family:'Barlow Semi Condensed',sans-serif;}
        ::-webkit-scrollbar{width:4px;}
        ::-webkit-scrollbar-track{background:#F2F0F0;}
        ::-webkit-scrollbar-thumb{background:#CC0033;border-radius:4px;}
        input,select,button{font-family:inherit;outline:none;}
        @keyframes spin{to{transform:rotate(360deg);}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:translateY(0);}}
        .fade{animation:fadeIn 0.35s ease forwards;}
      `}</style>

      <div className="fade" style={{ height:"100vh", display:"flex", flexDirection:"column" }}>

        {/* Utility Bar */}
        <div style={{ background:SBU.navy, padding:"5px 32px", display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
          <span style={{ fontFamily:"'Alumni Sans',sans-serif", fontSize:10, color:"rgba(255,255,255,0.35)", letterSpacing:"0.08em" }}>STONY BROOK UNIVERSITY</span>
          <a href="https://www.stonybrook.edu/ceas/" target="_blank" rel="noopener noreferrer" style={{ fontFamily:"'Barlow Semi Condensed',sans-serif", fontSize:10, color:"rgba(255,255,255,0.35)", textDecoration:"none" }}>CEAS WEBSITE ↗</a>
        </div>

        {/* Nav */}
        <nav style={{ background:SBU.white, borderBottom:`3px solid ${SBU.red}`, padding:"0 32px", display:"flex", alignItems:"center", gap:20, height:64, flexShrink:0, boxShadow:"0 2px 8px rgba(0,0,0,0.08)", position:"relative", overflow:"hidden" }}>
          {/* Subtle prism in nav */}
          <PrismCorner size={64} style={{ top:0, right:200, opacity:0.08, pointerEvents:"none" }} />

          {/* BrookMinds Logo */}
          <div onClick={reset} style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", flexShrink:0 }}>
            <div style={{ width:36, height:36, background:SBU.red, borderRadius:4, display:"flex", alignItems:"center", justifyContent:"center", position:"relative", overflow:"hidden" }}>
              <svg width="36" height="36" viewBox="0 0 36 36" style={{ position:"absolute", top:0, left:0 }}>
                <polygon points="36,0 36,36 18,0"  fill={SBU.redDark}   opacity="0.5" />
                <polygon points="36,0 36,18 24,0"  fill={SBU.redBright} opacity="0.4" />
              </svg>
              <span style={{ fontFamily:"'Bitter',Georgia,serif", fontSize:16, fontWeight:700, color:SBU.white, position:"relative", zIndex:1 }}>B</span>
            </div>
            <div>
              <div style={{ display:"flex", alignItems:"baseline", gap:1 }}>
                <span style={{ fontFamily:"'Bitter',Georgia,serif", fontSize:17, fontWeight:700, color:SBU.red, lineHeight:1 }}>Brook</span>
                <span style={{ fontFamily:"'Barlow Semi Condensed',sans-serif", fontSize:15, fontWeight:300, color:SBU.navy, lineHeight:1, letterSpacing:"0.04em" }}>Minds</span>
              </div>
              <div style={{ fontFamily:"'Alumni Sans',sans-serif", fontSize:9, color:SBU.midgray, marginTop:1, letterSpacing:"0.06em" }}>CEAS · STONY BROOK</div>
            </div>
          </div>

          {/* Search */}
          <div style={{ flex:1, maxWidth:480, display:"flex", alignItems:"center", gap:10, background:SBU.lightgray, border:`1px solid ${q?SBU.red:SBU.silver}`, borderRadius:4, padding:"0 14px", boxShadow:q?`0 0 0 3px ${SBU.red}22`:"none", transition:"all 0.15s" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={SBU.midgray} strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input value={q} onChange={e=>doSearch(e.target.value)} placeholder="Search: 'fluid dynamics', 'drug delivery for cancer', 'Prof. Ahn'..."
              style={{ flex:1, background:"transparent", border:"none", color:SBU.navy, fontSize:13, padding:"11px 0" }} />
            {q && <button onClick={()=>doSearch("")} style={{ background:"none", border:"none", color:SBU.midgray, cursor:"pointer", fontSize:17 }}>×</button>}
          </div>

          {/* Dept Filter */}
          <select value={dept} onChange={e=>{ const v=e.target.value; v==="All"?reset():pickDept(v); }}
            style={{ background:SBU.white, border:`1px solid ${SBU.silver}`, borderRadius:4, padding:"9px 12px", color:SBU.charcoal, fontSize:12, cursor:"pointer", flexShrink:0 }}>
            {["All",...Object.keys(DEPT_META)].map(d=><option key={d} value={d}>{d==="All"?"All Departments":d}</option>)}
          </select>

          {/* Stats */}
          <div style={{ flexShrink:0, fontFamily:"'Alumni Sans',sans-serif", fontSize:10, color:SBU.midgray, textAlign:"right", lineHeight:1.8 }}>
            <div><span style={{ color:SBU.red, fontWeight:700 }}>{faculty.length||"—"}</span> faculty</div>
            <div><span style={{ color:SBU.red, fontWeight:700 }}>9</span> departments</div>
          </div>
        </nav>

        {/* Body */}
        <div style={{ flex:1, display:"flex", overflow:"hidden" }}>
          {apiError && (
            <div style={{ position:"absolute", top:90, left:"50%", transform:"translateX(-50%)", zIndex:100, background:"#FFF3CD", border:"1px solid #FFCC00", borderRadius:6, padding:"12px 20px", fontFamily:"'Barlow Semi Condensed',sans-serif", fontSize:13, color:"#856404" }}>
              ⚠️ Backend not running. Start it: <code style={{ background:"rgba(0,0,0,0.08)", padding:"2px 6px", borderRadius:3 }}>uvicorn api.main:app --reload --port 8000</code>
            </div>
          )}

          {/* Sidebar */}
          {view==="list" && (
            <div style={{ width:310, flexShrink:0, display:"flex", flexDirection:"column", background:SBU.white, borderRight:`1px solid ${SBU.silver}`, overflow:"hidden" }}>
              <div style={{ padding:"10px 16px", borderBottom:`1px solid ${SBU.silver}`, background:SBU.lightgray, display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
                <button onClick={reset} style={{ background:"none", border:"none", cursor:"pointer", color:SBU.red, fontSize:12, fontFamily:"'IBM Plex Sans',sans-serif", fontWeight:600 }}>← All Departments</button>
                <SearchBadge
                  mode={searchMode === "searching" ? "keyword" : searchMode}
                  count={displayed.length}
                  suppressed={suppressed}
                />
              </div>

              {/* Searching indicator */}
              {searchMode === "searching" && (
                <div style={{ padding:"8px 16px", background:"#0F172A", display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ width:10, height:10, borderRadius:"50%", border:"2px solid #4ADE80", borderTop:"2px solid transparent", animation:"spin 0.7s linear infinite", flexShrink:0 }} />
                  <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:"#4ADE80" }}>AI searching...</span>
                </div>
              )}

              {/* Semantic mode banner */}
              {searchMode === "semantic" && semanticResults !== null && (
                <div style={{ padding:"8px 16px", background:"#0F172A", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color:"#4ADE80" }}>✦ Semantic search active</span>
                  <button onClick={() => { setSearchMode("keyword"); setSemanticResults(null); }}
                    style={{ background:"none", border:"none", cursor:"pointer", fontFamily:"'IBM Plex Sans',sans-serif", fontSize:10, color:"rgba(255,255,255,0.4)", textDecoration:"underline" }}>
                    use keyword
                  </button>
                </div>
              )}

              <div style={{ flex:1, overflowY:"auto" }}>
                {loading ? <Spinner/> :
                  displayed.length === 0
                    ? <NoResults q={q} mode={searchMode === "searching" ? "keyword" : searchMode} />
                    : searchMode === "semantic" && semanticResults !== null
                      ? displayed.map((r, i) => {
                          const f = r.faculty || r;
                          return <SemanticResultCard key={f.id ?? i} result={r} isSelected={sel?.id === f.id} onClick={() => setSel(f)} />;
                        })
                      : displayed.map(f => <FacultyRow key={f.id} f={f} isSelected={sel?.id === f.id} onClick={() => setSel(f)} />)
                }
              </div>
            </div>
          )}

          {view==="home"
            ?<DeptHome onPick={pickDept} deptStats={deptStats} totalFaculty={faculty.length} />
            :<Profile faculty={sel} />
          }
        </div>

        {/* Footer */}
        <div style={{ background:SBU.navy, padding:"10px 32px", display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0, position:"relative", overflow:"hidden" }}>
          <svg width="200" height="40" viewBox="0 0 200 40" style={{ position:"absolute", left:0, top:0, opacity:0.06 }}>
            <polygon points="0,0 80,0 0,40"   fill={SBU.red} />
            <polygon points="80,0 140,0 80,40 0,40" fill={SBU.redBright} />
          </svg>
          <span style={{ fontFamily:"'Alumni Sans',sans-serif", fontSize:9, color:"rgba(255,255,255,0.25)", letterSpacing:"0.06em", position:"relative" }}>
            © {new Date().getFullYear()} BROOKMINDS · STONY BROOK UNIVERSITY CEAS · POWERED BY OPENALEX
          </span>
          <div style={{ display:"flex", alignItems:"center", gap:16, position:"relative" }}>
            <ChatbotButton
              facultyList={faculty}
              onSelectFaculty={(f) => { setSel(f); setView("profile"); }}
            />
          </div>
        </div>
      </div>
    </>
  );
}

// ── Chatbot Component ───────────────────────────────────────────────────────

function ChatbotButton({ onSelectFaculty, facultyList = [] }) {
  const [open, setOpen]           = useState(false);
  const [messages, setMessages]   = useState([
    { role:"assistant", content:"Hi! I can help you find CEAS faculty by research area, expertise, or collaboration interest. What are you looking for?" }
  ]);
  const [input, setInput]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [remaining, setRemaining] = useState(20);
  const [sessionId]               = useState(() => "session_" + Date.now());
  const bottomRef                 = useRef(null);

  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior:"smooth" });
    }
  }, [messages, open]);

  const send = useCallback(async () => {
    const q = input.trim();
    if (!q || loading || remaining <= 0) return;
    setInput("");
    setLoading(true);

    const userMsg = { role:"user", content: q };
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs);

    try {
      const history = newMsgs.slice(-12).map(m => ({ role: m.role, content: m.content }));
      const res = await fetch(`${API}/chat`, {
        method:"POST",
        headers:{ "Content-Type":"application/json" },
        body: JSON.stringify({ message: q, history, session_id: sessionId })
      });
      const data = await res.json();
      setMessages(prev => [...prev, {
        role:    "assistant",
        content: data.response || "Sorry, I couldn't get a response.",
        cards:   data.faculty_cards || [],
      }]);
      if (data.remaining !== undefined) setRemaining(data.remaining);
    } catch(e) {
      setMessages(prev => [...prev, { role:"assistant", content:"Connection error — is the backend running?" }]);
    }
    setLoading(false);
  }, [input, loading, messages, remaining, sessionId]);

  const onKey = e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: SBU.red, color:"#fff", border:"none", borderRadius:"50%",
          width:44, height:44, display:"flex", alignItems:"center", justifyContent:"center",
          cursor:"pointer", fontSize:20, flexShrink:0,
        }}
        aria-label="Open AI Chatbot"
        title="AI Research Assistant"
      >
        {open ? "✕" : "💬"}
      </button>

      {/* Chat panel */}
      {open && (
        <div style={{
          position:"fixed", bottom:24, right:24, width:340,
          background:"#fff", border:"0.5px solid #e0e0e0",
          borderRadius:16, display:"flex", flexDirection:"column",
          boxShadow:"0 4px 24px rgba(0,0,0,0.12)", zIndex:9999,
          fontFamily:"'Barlow',sans-serif",
        }}>
          {/* Header */}
          <div style={{ background:SBU.red, borderRadius:"16px 16px 0 0", padding:"10px 14px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:28, height:28, borderRadius:"50%", background:"rgba(255,255,255,0.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>🔍</div>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:"#fff" }}>BrookMinds AI</div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.75)" }}>Ask about faculty research</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.8)", fontSize:18, cursor:"pointer", padding:0, lineHeight:1 }} aria-label="Close">✕</button>
          </div>

          {/* Messages */}
          <div style={{ flex:1, padding:12, display:"flex", flexDirection:"column", gap:8, maxHeight:320, overflowY:"auto" }}>
            {messages.map((m, i) => (
              <div key={i}>
                <div style={{
                  maxWidth:"85%", padding:"8px 10px", borderRadius:10, fontSize:12, lineHeight:1.5,
                  background: m.role === "user" ? SBU.red : "#f5f5f5",
                  color:      m.role === "user" ? "#fff" : "#222",
                  alignSelf:  m.role === "user" ? "flex-end" : "flex-start",
                  marginLeft: m.role === "user" ? "auto" : 0,
                }}>
                  {m.content}
                </div>
                {/* Faculty cards */}
                {m.cards && m.cards.length > 0 && (
                  <div style={{ display:"flex", flexDirection:"column", gap:4, marginTop:6 }}>
                    {m.cards.map((c, ci) => (
                      <div
                        key={ci}
                        onClick={() => {
                        const fullFaculty = facultyList.find(
                          f => f.name === c.name || f.name?.toLowerCase() === c.name?.toLowerCase()
                        );
                        if (onSelectFaculty) { onSelectFaculty(fullFaculty || c); }
                        setOpen(false);
                      }}
                        style={{
                          background:"#fff", border:"0.5px solid #e0e0e0", borderRadius:8,
                          padding:"6px 10px", cursor:"pointer", fontSize:11,
                          transition:"background 0.15s",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "#fef0f0"}
                        onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                      >
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                          <span style={{ fontWeight:600, color:"#222" }}>{c.name}</span>
                          <span style={{ fontSize:10, color:SBU.red, fontWeight:600 }}>{c.score}%</span>
                        </div>
                        <div style={{ color:"#666", fontSize:10, marginTop:1 }}>{c.dept}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div style={{ fontSize:11, color:"#888", padding:"4px 10px" }}>Searching faculty database...</div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Rate limit indicator */}
          <div style={{ textAlign:"center", fontSize:10, color:"#999", padding:"2px 0" }}>
            {remaining} messages remaining this session
          </div>

          {/* Input */}
          <div style={{ borderTop:"0.5px solid #eee", padding:"8px 10px", display:"flex", gap:6, alignItems:"center" }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={remaining > 0 ? "Ask about faculty research..." : "Session limit reached"}
              disabled={loading || remaining <= 0}
              style={{
                flex:1, border:"0.5px solid #ddd", borderRadius:20, padding:"6px 12px",
                fontSize:12, outline:"none", background: remaining > 0 ? "#f9f9f9" : "#f0f0f0",
              }}
              aria-label="Chat input"
            />
            <button
              onClick={send}
              disabled={loading || !input.trim() || remaining <= 0}
              style={{
                background: loading || !input.trim() || remaining <= 0 ? "#ccc" : SBU.red,
                color:"#fff", border:"none", borderRadius:"50%", width:30, height:30,
                display:"flex", alignItems:"center", justifyContent:"center",
                cursor: loading || !input.trim() || remaining <= 0 ? "not-allowed" : "pointer",
                fontSize:14, flexShrink:0,
              }}
              aria-label="Send message"
            >→</button>
          </div>
        </div>
      )}
    </>
  );
}
